import { useCallback, useEffect, useMemo, useState } from "react";
import { Trophy, Timer, ShieldCheck, Flame } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { useStratagems } from "@/context/StratagemContext";
import { useAuth } from "@/context/AuthContext";
import { formatMs, formatPercent } from "@/utils/format";
import { loadStats, resetStats } from "@/utils/storage";
import { fadeInUp, transition, transitionDelayed } from "@/utils/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, icon: Icon, testId }) => (
  <div className="hud-card clip-corners p-4" data-testid={testId}>
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-[#FFE800]" />
      <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{label}</p>
    </div>
    <p className="mt-3 font-data text-2xl text-white" data-testid={`${testId}-value`}>
      {value}
    </p>
  </div>
);

const Stats = () => {
  const { stratagems } = useStratagems();
  const { user } = useAuth();
  const [localStats, setLocalStats] = useState(loadStats());
  const [serverStats, setServerStats] = useState(null);

  useEffect(() => {
    setLocalStats(loadStats());
  }, [stratagems]);

  const fetchServerStats = useCallback(async () => {
    if (!user) { setServerStats(null); return; }
    try {
      const res = await axios.get(`${API}/practice/stats`);
      setServerStats(res.data);
    } catch {
      setServerStats(null);
    }
  }, [user]);

  useEffect(() => { fetchServerStats(); }, [fetchServerStats]);

  // Use server stats when available, fall back to local
  const stats = useMemo(() => {
    if (serverStats && serverStats.overall) {
      return {
        overall: {
          attempts: serverStats.overall.attempts || 0,
          successes: serverStats.overall.successes || 0,
          totalTimeMs: serverStats.overall.total_time_ms || 0,
          bestTimeMs: serverStats.overall.best_time_ms,
          longestStreak: serverStats.overall.longest_streak || 0,
        },
        byStratagem: Object.fromEntries(
          Object.entries(serverStats.by_stratagem || {}).map(([k, v]) => [
            k,
            {
              attempts: v.attempts || 0,
              successes: v.successes || 0,
              totalTimeMs: v.total_time_ms || 0,
              bestTimeMs: v.best_time_ms,
              bestStreak: v.best_streak || 0,
            },
          ]),
        ),
      };
    }
    return localStats;
  }, [serverStats, localStats]);

  const rows = useMemo(() => {
    return stratagems.map((stratagem) => {
      const entry = stats.byStratagem[stratagem.id] || {
        attempts: 0, successes: 0, totalTimeMs: 0, bestTimeMs: null,
      };
      const accuracy = entry.attempts ? entry.successes / entry.attempts : 0;
      const avgTime = entry.successes ? entry.totalTimeMs / entry.successes : 0;
      return { ...stratagem, ...entry, accuracy, avgTime };
    });
  }, [stratagems, stats]);

  const overallAccuracy = stats.overall.attempts
    ? stats.overall.successes / stats.overall.attempts
    : 0;
  const overallAvgTime = stats.overall.successes
    ? stats.overall.totalTimeMs / stats.overall.successes
    : 0;

  const handleReset = () => {
    const confirmed = window.confirm("Clear all local training stats?");
    if (confirmed) setLocalStats(resetStats());
  };

  const isServerSource = Boolean(serverStats && serverStats.overall);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 md:px-12" data-testid="stats-page">
      <motion.section
        {...fadeInUp}
        transition={transition}
        className="hud-card clip-corners p-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1
              className="font-headings text-4xl uppercase tracking-widest text-[#FFE800]"
              data-testid="stats-title"
            >
              War Log
            </h1>
            <p className="text-sm text-gray-400" data-testid="stats-subtitle">
              {isServerSource
                ? "Your stats are synced across all your devices."
                : "Your progress is stored locally on this machine only."}
            </p>
          </div>
          {!isServerSource && (
            <button
              onClick={handleReset}
              className="border border-red-500/40 px-4 py-2 text-xs font-data text-red-400 hover:border-red-400"
              data-testid="clear-stats-button"
            >
              Clear Local Stats
            </button>
          )}
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-4">
          <StatCard label="Accuracy" value={formatPercent(overallAccuracy)} icon={ShieldCheck} testId="stats-overall-accuracy" />
          <StatCard label="Avg Time" value={formatMs(overallAvgTime)} icon={Timer} testId="stats-overall-avg" />
          <StatCard label="Best Time" value={formatMs(stats.overall.bestTimeMs)} icon={Trophy} testId="stats-overall-best" />
          <StatCard label="Longest Streak" value={stats.overall.longestStreak || 0} icon={Flame} testId="stats-overall-streak" />
        </div>
      </motion.section>

      <motion.section
        {...fadeInUp}
        transition={transitionDelayed}
        className="mt-8 hud-card clip-corners p-6"
      >
        <h2 className="font-headings text-2xl uppercase tracking-widest text-white mb-4" data-testid="stats-breakdown-title">
          Stratagem Breakdown
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="stats-table">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-[0.3em] text-gray-500">
                <th className="py-3 pr-4">Stratagem</th>
                <th className="py-3 pr-4 text-right">Attempts</th>
                <th className="py-3 pr-4 text-right">Successes</th>
                <th className="py-3 pr-4 text-right">Accuracy</th>
                <th className="py-3 pr-4 text-right">Avg Time</th>
                <th className="py-3 text-right">Best Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.filter((r) => r.attempts > 0).map((row) => (
                <tr key={row.id} className="border-b border-white/5" data-testid={`stats-row-${row.id}`}>
                  <td className="py-2 pr-4 font-headings uppercase tracking-wide text-white">{row.name}</td>
                  <td className="py-2 pr-4 text-right font-data text-gray-400">{row.attempts}</td>
                  <td className="py-2 pr-4 text-right font-data text-gray-400">{row.successes}</td>
                  <td className="py-2 pr-4 text-right font-data text-[#FFE800]">{formatPercent(row.accuracy)}</td>
                  <td className="py-2 pr-4 text-right font-data text-gray-400">{formatMs(row.avgTime)}</td>
                  <td className="py-2 text-right font-data text-green-400">{formatMs(row.bestTimeMs)}</td>
                </tr>
              ))}
              {rows.filter((r) => r.attempts > 0).length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No attempts recorded yet. Head to the Drill page to start training.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.section>
    </div>
  );
};

export default Stats;
